import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup, Validators} from "@angular/forms";
import {HttpClient} from "@angular/common/http";
import {PortalService} from "../services/portal.service";
import {ElementService} from "../services/element.service";

@Component({
  selector: 'app-dialogform',
  templateUrl: './dialogform.component.html'
})
export class DialogformComponent implements OnInit {

  /**
   * prefix for all textkeys that are used inside this component.
   */
  @Input() textkeyPrefix: string;

  /**
   * Extra Postfix that is optional on every text
   */
  @Input() textkeyPostfix: string = "";

  /**
   * additional data for the regisztration of the user
   */
  @Input() registrationData: any = {};

  /**
   * Download URL for the report. If not set, download is not shown
   */
  @Input() downloadUrl: string = null;

  /**
   * textkey for the copyright. If set to null, no copyright is shown
   */
  @Input() copyright: string = '.dialog.copyright';

  /**
   * URL for the image that should be shown near the list
   */
  @Input() advisorImagePath: string = '/moc/naechsteschritte-dialog.jpeg';

  /**
   * Defines the breakpoint for the large viewport. Can be overwritten for caeses where this element
   * is near a sidebar and needs to show the large viewpoint later
   */
  @Input() largeBreakpoint: string = 'large';

  /**
   * Defines the breakpoint for the medium viewport. Can be overwritten for caeses where this element
   * is near a sidebar and needs to show the large viewpoint later
   */
  @Input() mediumBreakpoint: string = 'medium';

  /**
   * Eventimitter that is called, if a registration is finished. Must not be successfull but a result from the backend was received.
   */
  @Output() onDialogRegister: EventEmitter<string> = new EventEmitter<string>();

  /**
   * The form errors element
   */
  @ViewChild('formErrors') formErrors: ElementRef;

  /**
   * The result wrapper element
   */
  @ViewChild('resultWrapper') resultWrapper: ElementRef;

  /**
   * List of resultcodes from the api that we interprete as success
   */
  readonly apiSuccessResults: string[] = ['ok', 'duplicate', 'eventmail'];

  /**
   * Formgroup for input validation
   */
  form: UntypedFormGroup;

  /**
   * Toggle for showing an extra checkbox for the terms
   */
  showCheckbox: boolean = false;

  /**
   * toggle if the terms are selected or not.
   */
  terms: boolean = true;

  /**
   * error textkey
   */
  error: string = null;

  /**
   * Result of the registration
   */
  result: string = null;

  /**
   * toggle if this is an internal project or not.
   */
  internalPortal: boolean;

  /**
   * fields for salutation, name and firstname should be visible
   */
  showName: boolean = false;

  /**
   * Options for the salutaion dropdown
   */
  salutationOptions = ['frau', 'herr', 'divers'];

  /**
   * indicates whether mandatory fields are marked.
   */
  markMandatoryFields: boolean = false;

  /**
   * list of mandatory contactfields
   */
  mandatoryFields: string[] = [];

  /**
   * specifies whether to use an external API instead of Broadmail.
   */
  useExternalApi: boolean = false;

  /**
   * Hide image
   */
  hideimage: boolean = false;

  /**
   * constructor
   */
  constructor(private http: HttpClient, private portalService: PortalService, private elementService: ElementService) {}

  /**
   * @inheritDoc
   */
  ngOnInit(): void {
    // init the form
    this.form = new UntypedFormGroup({
      'dialogform-email': new UntypedFormControl('', [Validators.required, Validators.email])
    });

    // should be use a checkbox or terms link for the lead form
    this.showCheckbox = this.portalService.getConfigAsBool("embed.newsletter.checkbox");
    if(this.showCheckbox === true) {
      this.form.addControl('dialogform-terms', new UntypedFormControl('', [Validators.requiredTrue]))
    }

    this.mandatoryFields = this.portalService.getConfig('embed.newsletter.mandatory_fields')?
      this.portalService.getConfig('embed.newsletter.mandatory_fields').split(',') : [];

    // should be used fields for salutation and names?
    this.showName = this.portalService.getConfigAsBool('embed.newsletter.name');
    if (this.showName === true) {
      ['salutation', 'firstname', 'lastname'].forEach(fieldName => {
        this.form.addControl('dialogform-' + fieldName, new UntypedFormControl('', this.mandatoryFields.indexOf(fieldName) > -1? Validators.required : []));
      });
    }

    // should mandatory fields be marked
    this.markMandatoryFields = this.portalService.getConfigAsBool('embed.newsletter.mark_mandatory_fields');

    this.internalPortal = this.portalService.isInternalPortal();
    this.useExternalApi = this.portalService.getConfigAsBool('embed.newsletter.use_external_api');
    this.hideimage = this.portalService.getConfigAsBool('embed.newsletter.hideimage');
  }

  /**
   * click on submit button
   */
  submit(): void {
    this.error = null;
    if(this.form.valid === false) {
      if(this.showCheckbox === true && this.form.get('dialogform-terms').valid === false) {
        this.error = 'terms';
      }
      else {
        this.error = 'email';
      }
      // scroll to error
      setTimeout(() => {
        this.elementService.scrollToElement(this.formErrors.nativeElement);
      }, 200);
      return;
    }

    const url = this.useExternalApi ? '/dialog/registration/external' : '/dialog/registration';
    if (this.useExternalApi === true) {
      this.registrationData.newsletter = true;
    }

    // should the name be visible
    if (this.showName === true) {
      this.registrationData.salutation = this.form.get('dialogform-salutation').value;
      this.registrationData.firstname = this.form.get('dialogform-firstname').value;
      this.registrationData.lastname = this.form.get('dialogform-lastname').value;
    }

    // save data and show result
    this.http.post(url, {...this.registrationData, ...{email: this.form.get('dialogform-email').value}}).subscribe(response => {
      if(response.hasOwnProperty('result')) {
        if(this.apiSuccessResults.indexOf(response['result']) !== -1) {
          this.result = response['result'];
          // scroll to form-wrapper
          setTimeout(() => {
            this.elementService.scrollToElement(this.resultWrapper.nativeElement);
          }, 200);
        }
        else {
          this.internalError(response['result']);
        }
        this.onDialogRegister.emit(this.result);
      }
      else {
        this.internalError('internal', response);
        this.onDialogRegister.emit('internalerror');
      }
    },
    error => this.internalError(error));
  }

  /**
   * Result of registration was an internal error
   * @param error
   * @param message
   */
  internalError(error: string = 'internal', message: any = null): void {
    if(typeof error === "object") {
      // in some error cases, the error is an object (i.e. 500 eror from backend)
      error = 'internal';
    }
    this.error = error;
    setTimeout(() => {
      this.elementService.scrollToElement(this.formErrors.nativeElement);
    }, 200);
    if(message) {
      throw Error('could not register email. ' + JSON.stringify(message));
    }
  }

}
